// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ProductType, getProductTypeKey } from '@shared/utils/Instruments/ProductType';
import { DataCache } from '../../DataCache';
import { Resources } from '@shared/localizations/Resources';
import { BaseSettings } from '../../Settings/BaseGeneralSettingsWrapper';
const NOT_AVAILABLE = '-';
export class PositionsBalanceFormatter {
    static SymbolStr(positionsBalance) {
        if (positionsBalance.Position.ProductType === ProductType.CorporateAction) {
            return positionsBalance.Position.GetInstrumentDisplayName(true);
        }
        else {
            return positionsBalance.Instrument.DisplayShortName();
        }
    }
    static NetPositionQty(positionsBalance) {
        const netPositionQty = positionsBalance.NetPositionQty;
        if (BaseSettings.isQuantityInLots) {
            return netPositionQty;
        }
        else {
            return netPositionQty * positionsBalance.Instrument.LotSize;
        }
    }
    static NetPositionQtyStr(positionsBalance) {
        const netPositionQty = this.NetPositionQty(positionsBalance);
        return DataCache.formatVolume(positionsBalance.Instrument, netPositionQty, BaseSettings.isQuantityInLots, positionsBalance.ProductType, positionsBalance.getAccountWithMaxAmountPrecision());
    }
    static GrossPnL(positionsBalance) {
        return positionsBalance.GrossPnL;
    }
    static GrossPnLStr(positionsBalance) {
        var _a;
        const grossPnL = this.GrossPnL(positionsBalance);
        return (_a = positionsBalance.Asset) === null || _a === void 0 ? void 0 : _a.formatPrice(grossPnL);
    }
    static CurrentPrice(positionsBalance) {
        return positionsBalance.CurrentPrice;
    }
    static CurrentPriceStr(positionsBalance) {
        const currentPrice = this.CurrentPrice(positionsBalance);
        return isNaN(currentPrice) ? NOT_AVAILABLE : positionsBalance.Instrument.formatPrice(currentPrice);
    }
    static BreakEven(positionsBalance) {
        return positionsBalance.BreakEvenPoint;
    }
    static BreakEvenStr(positionsBalance) {
        const breakevenPoint = this.BreakEven(positionsBalance);
        return isNaN(breakevenPoint)
            ? NOT_AVAILABLE
            : positionsBalance.Instrument.formatPrice(breakevenPoint, true, true);
    }
    static GrossExposure(positionsBalance) {
        return positionsBalance.GrossExposure;
    }
    static GrossExposureStr(positionsBalance) {
        var _a;
        const grossExposure = this.GrossExposure(positionsBalance);
        return (_a = positionsBalance.Asset) === null || _a === void 0 ? void 0 : _a.formatPrice(grossExposure);
    }
    static NetExposure(positionsBalance) {
        return positionsBalance.NetExposure;
    }
    static NetExposureStr(positionsBalance) {
        var _a;
        const netExposure = this.NetExposure(positionsBalance);
        return (_a = positionsBalance.Asset) === null || _a === void 0 ? void 0 : _a.formatPrice(netExposure);
    }
    static LongQty(positionsBalance) {
        const longQty = positionsBalance.LongQty;
        if (BaseSettings.isQuantityInLots) {
            return longQty;
        }
        else {
            return longQty * positionsBalance.Instrument.LotSize;
        }
    }
    static LongQtyStr(positionsBalance) {
        const longQty = this.LongQty(positionsBalance);
        return DataCache.formatVolume(positionsBalance.Instrument, Math.abs(longQty), BaseSettings.isQuantityInLots, positionsBalance.ProductType, positionsBalance.getAccountWithMaxAmountPrecision());
    }
    static ShortQty(positionsBalance) {
        const shortQty = positionsBalance.ShortQty;
        if (BaseSettings.isQuantityInLots) {
            return shortQty;
        }
        else {
            return shortQty * positionsBalance.Instrument.LotSize;
        }
    }
    static ShortQtyStr(positionsBalance) {
        const shortQty = this.ShortQty(positionsBalance);
        return DataCache.formatVolume(positionsBalance.Instrument, Math.abs(shortQty), BaseSettings.isQuantityInLots, positionsBalance.ProductType, positionsBalance.getAccountWithMaxAmountPrecision());
    }
    static AverageLong(positionsBalance) {
        return positionsBalance.AverageLong;
    }
    static AverageLongStr(positionsBalance) {
        const averageLong = this.AverageLong(positionsBalance);
        return isNaN(averageLong) ? NOT_AVAILABLE : positionsBalance.Instrument.formatPrice(averageLong);
    }
    static AverageShort(positionsBalance) {
        return positionsBalance.AverageShort;
    }
    static AverageShortStr(positionsBalance) {
        const averageShort = this.AverageShort(positionsBalance);
        return isNaN(averageShort) ? NOT_AVAILABLE : positionsBalance.Instrument.formatPrice(averageShort);
    }
    static PositionValue(positionsBalance) {
        return positionsBalance.PositionValue;
    }
    static PositionValueStr(positionsBalance) {
        var _a;
        const positionValue = this.PositionValue(positionsBalance);
        return (_a = positionsBalance.Asset) === null || _a === void 0 ? void 0 : _a.formatPrice(positionValue);
    }
    static ProductTypeStr(positionsBalance) {
        const productType = positionsBalance.Position.ProductType;
        return Resources.getResource('ProductType.' + getProductTypeKey(productType));
    }
}
